import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useFetch } from '@api/hooks';
import { selectDashboardPerScopeValueList } from '@reducers/v2/dashboard/dashboard.slice';
import { fetchDashboardPerScopeValueList } from '@reducers/v2/dashboard/thunks';
import type { DashboardScope, DashboardResultType, DashboardResultOrder, DashboardDrilldownFilter } from '@api/domain/types';

export interface UseGetDashboardPerScopeValueListParams {
  scope: DashboardScope;
  resultType: DashboardResultType;
  from: string;
  to: string;
  orderBy: DashboardResultOrder;
  drilldownFilter?: DashboardDrilldownFilter;
}

export const useGetDashboardPerScopeValueList = ({
  scope,
  resultType,
  from,
  to,
  orderBy,
  drilldownFilter,
}: UseGetDashboardPerScopeValueListParams) => {
  const dispatch = useAppDispatch();
  const { items, pageNumber, hasMore, loading: storeLoading, error } = useSelector(selectDashboardPerScopeValueList);

  const { loading, refetch } = useFetch<null>({
    fetchFn: async (signal) => {
      await dispatch(
        fetchDashboardPerScopeValueList({
          params: { scope, resultType, from, to, orderBy, drilldownFilter, pageNumber: 1 },
          append: false,
          signal,
        })
      ).unwrap();
      return null;
    },
    deps: [scope, resultType, from, to, orderBy, drilldownFilter ? JSON.stringify(drilldownFilter) : null],
  });

  const loadNext = useCallback(() => {
    if (!hasMore || storeLoading || loading) return;
    dispatch(
      fetchDashboardPerScopeValueList({
        params: { scope, resultType, from, to, orderBy, drilldownFilter, pageNumber: pageNumber + 1 },
        append: true,
      })
    );
  }, [dispatch, hasMore, storeLoading, loading, pageNumber, scope, resultType, from, to, orderBy, drilldownFilter]);

  return {
    items,
    hasMore,
    loading: loading || storeLoading,
    error,
    loadNext,
    refetch,
  };
};
